import { Link } from "wouter";

import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE = "May 12, 2026";

const SECTIONS = [
  {
    title: "1. Introduction",
    content: `Zebiso Intelligence, Inc. ("Zindeal," "we," "us," or "our") operates the Zindeal coach contract management platform (the "Service"). This Privacy Policy explains how we collect, use, disclose, and protect information when you use our Service.

By using the Service, you agree to the collection and use of information in accordance with this policy. If you are using the Service on behalf of an institution, you represent that you have authority to bind that institution to this policy.`,
  },
  {
    title: "2. Information We Collect",
    content: `We collect information you provide directly, information generated by your use of the Service, and information from third-party services.

Account and Profile Information
When you create an account, we collect your name, email address, institution name, division, and conference. We may also collect additional profile information you choose to provide.

Customer Data
The Service allows you to store coaching contract details, coach profiles, insurance policy information, salary data, and other athletic department records ("Customer Data"). This data is provided by you and belongs to you.

Usage and Log Data
We automatically collect information about how you use the Service, including pages visited, features used, timestamps, IP addresses, browser type and version, and device identifiers. This data helps us improve the Service and diagnose issues.

Payment Information
When you subscribe to a paid plan, payment processing is handled by Stripe. We receive confirmation of payment and your billing plan but do not store full credit card numbers. Please review Stripe's privacy policy at stripe.com/privacy.

Cookies and Tracking
We use cookies and similar technologies to maintain your session, remember preferences, and analyze usage patterns. You can control cookie settings through your browser; disabling cookies may affect Service functionality.`,
  },
  {
    title: "3. How We Use Your Information",
    content: `We use the information we collect to:

• Provide, operate, maintain, and improve the Service
• Process transactions and send related information including purchase confirmations and invoices
• Send administrative communications such as security alerts, product updates, and support messages
• Respond to your comments, questions, and requests
• Monitor and analyze usage patterns to improve user experience
• Detect, investigate, and prevent fraudulent transactions and other illegal activities
• Comply with legal obligations

We do not sell your personal information or Customer Data to third parties. We do not use Customer Data for advertising purposes.`,
  },
  {
    title: "4. Third-Party Services (Subprocessors)",
    content: `We engage the following third-party service providers ("subprocessors") to operate the Service. A current, machine-readable list (with the categories of personal data shared with each) is also maintained at /subprocessors.

Clerk, Inc. (Authentication & Identity)
Processes your email address, name, profile image, and authentication credentials (including passwords, SSO/OIDC tokens, and MFA factors). Hosted in the United States. Review Clerk's privacy policy at clerk.com/legal/privacy.

Stripe, Inc. (Payments & Billing)
Processes your billing name, billing address, email, payment card information, tax ID where applicable, subscription plan, and transaction history. We do not store full card numbers ourselves. Hosted in the United States. Review Stripe's privacy policy at stripe.com/privacy.

Resend (Transactional & Notification Email)
Sends authentication codes, contract notifications, lifecycle reminders, demo verification codes, and other transactional email on our behalf. Receives the recipient email address, sender, subject, and message body, plus delivery and bounce events. Hosted in the United States. Review Resend's privacy policy at resend.com/legal/privacy-policy.

Google Cloud Storage (File & Document Storage)
Stores uploaded contract PDFs, clause images, and other file attachments you submit to the Service. Files are encrypted at rest and accessed only by the Service on your behalf. Hosted in the United States. Review Google Cloud's privacy notice at cloud.google.com/terms/cloud-privacy-notice.

Replit, Inc. (Hosting & Managed Database)
The Service runs on Replit Deployments and uses Replit Managed Postgres for primary data storage and automated backups. Receives all Customer Data and account data necessary to operate the Service. Hosted in the United States. Review Replit's privacy policy at replit.com/site/privacy.

Anthropic, PBC (AI Contract Extraction — Optional Feature)
When you use the PDF import / AI extraction feature, the contents of the uploaded contract are sent to Anthropic's Claude API to extract structured terms. Anthropic processes the document only to return the extraction and does not train models on your data under our API agreement. Review Anthropic's privacy policy at anthropic.com/legal/privacy.

We require all subprocessors to maintain at least the same level of privacy and security protection as described in this policy and to use your data only to provide the specific services we have contracted them to perform. We will provide at least 30 days' notice of any new subprocessor by updating the list at /subprocessors. To receive email notifications of changes, contact privacy@zindeal.com.`,
  },
  {
    title: "5. Data Sharing and Disclosure",
    content: `We may share your information in the following circumstances:

With Your Consent
We may share your information with third parties when you have given us explicit consent to do so.

Service Providers
We share information with vendors and service providers who assist in operating the Service, subject to confidentiality obligations.

Business Transfers
If Zindeal is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will provide notice of such a transfer and any material changes to this policy.

Legal Requirements
We may disclose your information if required to do so by law, court order, or government authority, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.

We do not share Customer Data with other Zindeal customers or with any third party for their independent use.`,
  },
  {
    title: "6. Data Retention",
    content: `We retain personal information and Customer Data for as long as your account is active or as needed to provide the Service. Specifically:

• Active accounts: Data is retained for the duration of your subscription plus 90 days after cancellation.
• During the 90-day post-cancellation window, you may export your data in supported formats.
• After 90 days following cancellation, Customer Data is permanently deleted from our production systems.
• Backup copies may persist for up to 30 additional days before being purged from backup systems.
• We retain certain logs and usage records for up to 12 months for security and fraud prevention purposes.
• We retain billing records as required by applicable law (typically 7 years).

You may request deletion of your account and associated data at any time by contacting privacy@zindeal.com.`,
  },
  {
    title: "7. Data Security",
    content: `We implement commercially reasonable technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. In plain language:

• Private per-tenant workspace. Every athletic department gets its own logically isolated workspace. Customer Data from one institution is never visible to another customer; our API enforces tenant scoping on every read and every write, including defense-in-depth checks on update and delete operations.
• Encryption in transit and at rest. All traffic to and from the Service is encrypted using TLS 1.2 or higher. Customer Data and file uploads are encrypted at rest in our managed database and object storage.
• Single sign-on (SSO). Enterprise plans support SAML/OIDC SSO with Okta, Azure Active Directory, and Google Workspace, so identity and offboarding stay under your IT team's control.
• Audit logging. Every contract draft, edit, comment, approval, e-signature, file upload, and administrative action is logged automatically with the actor, timestamp, IP address, and resulting state — and can be exported on demand for internal compliance, an NCAA review, or your board.
• Access controls limiting employee access to Customer Data on a need-to-know basis, plus mandatory MFA for any administrative access.
• Regular security assessments and vulnerability scanning, with documented incident response procedures.

No method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security. In the event of a data breach that affects your rights and freedoms, we will notify you as required by applicable law.`,
  },
  {
    title: "8. Your Rights and Choices",
    content: `Depending on your jurisdiction, you may have the following rights regarding your personal information:

Access and Portability
You may request a copy of the personal information we hold about you and, where technically feasible, receive it in a structured, machine-readable format.

Correction
You may update or correct inaccurate personal information through your account settings or by contacting us.

Deletion
You may request deletion of your personal information. Note that we may need to retain certain information for legal or legitimate business purposes.

Objection and Restriction
You may object to or request restriction of certain processing activities.

Withdrawal of Consent
Where processing is based on your consent, you may withdraw consent at any time without affecting the lawfulness of prior processing.

To exercise any of these rights, contact us at privacy@zindeal.com. We will respond to requests within 30 days. We may need to verify your identity before processing your request.`,
  },
  {
    title: "9. Children's Privacy",
    content: `The Service is not directed to children under the age of 16. We do not knowingly collect personal information from children under 16. If you become aware that a child has provided us with personal information, please contact us at privacy@zindeal.com and we will take steps to delete such information.`,
  },
  {
    title: "10. International Data Transfers",
    content: `Zindeal is based in the United States. If you access the Service from outside the United States, your information may be transferred to and processed in the United States, which may have different data protection laws than your country of residence.

By using the Service, you consent to the transfer of your information to the United States. For users in the European Economic Area or United Kingdom, we rely on Standard Contractual Clauses approved by the European Commission to transfer data lawfully.`,
  },
  {
    title: "11. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by email at least 30 days before they take effect. The "Effective date" at the top of this page indicates when the policy was last updated.

Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated policy.`,
  },
  {
    title: "12. Contact Us",
    content: `If you have questions, concerns, or requests related to this Privacy Policy or our data practices, please contact us:

Privacy Team — Zebiso Intelligence, Inc.
privacy@zindeal.com
support@zindeal.com

We are committed to working with you to resolve any privacy concerns.`,
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <nav className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full" />
            <span className="font-serif font-semibold text-lg tracking-tight">Zindeal</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/sign-in">Sign In</Link>
            </Button>
            <Button size="sm" className="bg-[#0B1834] hover:bg-[#0A152E] text-white" asChild>
              <Link href="/sign-up">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-gray-500 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          <p className="mt-4 text-gray-600 leading-relaxed">
            This Privacy Policy describes how Zebiso Intelligence, Inc. collects, uses, and protects your information when you use our workforce intelligence platform for college athletics. We treat your coach contracts and the personal data inside them as the sensitive records they are — your department remains the owner of all Customer Data, and we use it only to operate the Service for you.
          </p>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg font-bold text-gray-900 mb-3">{section.title}</h2>
              <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-gray-100 py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-6 h-6 rounded-full" />
            <span className="font-semibold text-gray-700">Zindeal</span>
          </div>
          <div className="flex gap-6">
            <Link href="/terms" className="hover:text-gray-700 transition-colors">Terms of Service</Link>
            <Link href="/privacy" className="hover:text-gray-700 transition-colors font-medium text-[#0B1834]">Privacy Policy</Link>
            <Link href="/subprocessors" className="hover:text-gray-700 transition-colors">Subprocessors</Link>
            <Link href="/sign-in" className="hover:text-gray-700 transition-colors">Sign In</Link>
          </div>
          <span>© {new Date().getFullYear()} Zebiso Intelligence, Inc. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
